import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {Select, Store} from '@ngxs/store';
import {Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {OverlayPanel} from 'primeng/overlaypanel';
import {SesionState} from '@app/state/sesion/sesion.state';
import {SetCompanySelected, SetOffice, SetSaleStation} from '@app/state/sesion/sesion.actions';
import {Company} from '@app/domain/company';
import {Office} from '@app/domain/office';
import {SaleStation} from '@app/domain/sale-station';
import {ShortcutMenu} from '@app/domain/shortcut-menu';
import {LoginService} from '@app/service/login.service';
import {SaleStationService} from '@app/service/sale-station.service';

@Component({
    selector: 'app-context-switcher',
    templateUrl: './context-switcher.component.html',
    styleUrls: ['./context-switcher.component.scss']
})
export class ContextSwitcherComponent implements OnInit, OnDestroy {
    private unsubscribe$ = new Subject<void>();

    @Select(SesionState.getCompany) company$: Observable<Company>;
    @Select(SesionState.getCompanySelected) companySelected$: Observable<Company>;
    @Select(SesionState.getOffice) office$: Observable<Office>;
    @Select(SesionState.getSaleStation) saleStation$: Observable<SaleStation>;

    // valores aplicados (los que muestra el resumen del header)
    company: Company;
    companySelected: Company;
    office: Office;
    saleStation: SaleStation;

    // valores de trabajo dentro del panel; se aplican solo al confirmar
    empresaSel: Company;
    officeSel: Office;
    stationSel: SaleStation;

    attachedCompanies: Company[] = [];
    offices: Office[] = this.loginService.getPuntosVentaUsuario();
    stations: SaleStation[] = [];
    loadingStations = false;
    private stationsForOfficeId: any = null;

    panelOpen = false;
    sheetVisible = false;

    // selector rápido por segmento (empresa / sede / estación)
    segMode: 'empresa' | 'sede' | 'estacion' = 'sede';

    // secciones extra del bottom sheet en móvil
    @Input() shortcuts: ShortcutMenu[] = [];
    @Input() printerVisible = false;
    @Input() printerConnected = false;
    @Output() printerClick = new EventEmitter<void>();
    @Input() whatsappVisible = false;
    @Input() whatsappConnected = false;
    @Input() whatsappRoute: string;

    @ViewChild('panel') panel: OverlayPanel;
    @ViewChild('segPanelEmpresa') segPanelEmpresa: OverlayPanel;
    @ViewChild('segPanelSede') segPanelSede: OverlayPanel;
    @ViewChild('segPanelEstacion') segPanelEstacion: OverlayPanel;

    constructor(private loginService: LoginService,
                private saleStationService: SaleStationService,
                private store: Store) { }

    ngOnInit(): void {
        this.company$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(c => {
                this.company = c;
                this.attachedCompanies = [this.company, ...(this.loginService.getUser().empresasAdjuntas || [])];
            });
        this.companySelected$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(c => this.companySelected = c);
        this.office$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(o => this.office = o);
        this.saleStation$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(s => this.saleStation = s);
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    get canSelectCompany() {
        return this.loginService.hasPermission('SELECCIONAR_EMPRESAS_ADJUNTAS') && this.attachedCompanies.length > 1;
    }

    get isMobile() {
        return window.matchMedia('(max-width: 991px)').matches;
    }

    open(event: Event) {
        event.preventDefault();
        this.hideSegPanels();
        this.initWorkingValues();
        if (this.isMobile) {
            this.sheetVisible = true;
        } else {
            this.panel.toggle(event);
            this.panelOpen = true;
        }
    }

    // --- selector rápido: clic directo sobre un segmento del resumen ---

    openSegment(seg: 'empresa' | 'sede' | 'estacion', event: Event) {
        event.preventDefault();
        // que el click no llegue al listener de documento de los overlays
        event.stopPropagation();
        if (this.isMobile) {
            // en móvil el chip abre el sheet completo
            this.open(event);
            return;
        }
        if (seg === 'empresa' && !this.canSelectCompany) {
            return;
        }
        if (this.panel) {
            this.panel.hide();
            this.panelOpen = false;
        }
        this.hideSegPanels(seg);
        this.syncWorkingValues();
        this.segMode = seg;
        if (seg === 'estacion' && this.stationsForOfficeId !== this.office?.id) {
            this.loadStations(this.office, this.saleStation, false);
        }
        this.segPanelFor(seg)?.toggle(event, event.currentTarget as any);
    }

    private segPanelFor(seg: 'empresa' | 'sede' | 'estacion'): OverlayPanel {
        return seg === 'empresa' ? this.segPanelEmpresa : seg === 'sede' ? this.segPanelSede : this.segPanelEstacion;
    }

    private hideSegPanels(except?: 'empresa' | 'sede' | 'estacion') {
        (['empresa', 'sede', 'estacion'] as const)
            .filter(seg => seg !== except)
            .forEach(seg => this.segPanelFor(seg)?.hide());
    }

    get segTitle() {
        return this.segMode === 'empresa' ? 'Empresa' : this.segMode === 'sede' ? 'Sede / Punto de venta' : 'Estación';
    }

    get segOptions(): any[] {
        return this.segMode === 'empresa' ? this.attachedCompanies : this.segMode === 'sede' ? this.offices : this.stations;
    }

    segLabel(item: any) {
        return this.segMode === 'empresa' ? item?.razonSocial : item?.nombre;
    }

    isCurrentSegItem(item: any) {
        const actual = this.segMode === 'empresa' ? this.companySelected : this.segMode === 'sede' ? this.office : this.saleStation;
        return item?.id === actual?.id;
    }

    selectSegItem(item: any) {
        if (this.segMode === 'empresa') {
            this.empresaSel = item;
            this.onEmpresaChange();
        } else if (this.segMode === 'sede') {
            this.officeSel = item;
            this.onOfficeChange();
        } else {
            this.stationSel = item;
            this.dispatchStation();
        }
        this.hideSegPanels();
    }

    private syncWorkingValues() {
        this.empresaSel = this.attachedCompanies.find(c => c?.id === this.companySelected?.id) || this.companySelected;
        this.officeSel = this.offices.find(o => o?.id === this.office?.id) || this.office;
        this.stationSel = this.saleStation;
    }

    close() {
        this.sheetVisible = false;
        this.panelOpen = false;
        if (this.panel) {
            this.panel.hide();
        }
    }

    onPanelHide() {
        this.panelOpen = false;
    }

    onEmpresaChange() {
        this.dispatchCompany();
        const officeAsignado = this.offices.find(o => o.rucAsignado === this.empresaSel?.ruc);
        if (officeAsignado) {
            this.officeSel = officeAsignado;
            this.dispatchOffice();
            this.loadStations(this.officeSel, null, true);
        }
    }

    onOfficeChange() {
        // espejo del acople existente: la sede con RUC asignado arrastra la empresa
        if (this.officeSel?.rucAsignado) {
            const empresa = this.attachedCompanies.find(ea => ea.ruc === this.officeSel.rucAsignado);
            if (empresa) {
                this.empresaSel = empresa;
                this.dispatchCompany();
            }
        }
        this.dispatchOffice();
        this.loadStations(this.officeSel, null, true);
    }

    onStationChange() {
        this.dispatchStation();
    }

    private dispatchCompany() {
        if (this.empresaSel && this.empresaSel.id !== this.companySelected?.id) {
            this.store.dispatch(new SetCompanySelected(this.empresaSel));
        }
    }

    private dispatchOffice() {
        if (this.officeSel && this.officeSel.id !== this.office?.id) {
            this.store.dispatch(new SetOffice({office: this.officeSel}));
        }
    }

    private dispatchStation() {
        if (this.stationSel && this.stationSel.id !== this.saleStation?.id) {
            this.store.dispatch(new SetSaleStation({saleStation: this.stationSel}));
        }
    }

    private initWorkingValues() {
        this.empresaSel = this.attachedCompanies.find(c => c?.id === this.companySelected?.id) || this.companySelected;
        this.officeSel = this.offices.find(o => o?.id === this.office?.id) || this.office;
        this.loadStations(this.officeSel, this.saleStation, false);
    }

    private loadStations(office: Office, keepCurrent: SaleStation, dispatchOnLoad: boolean) {
        this.stations = [];
        this.stationSel = null;
        this.stationsForOfficeId = office?.id ?? null;
        if (!office) {
            return;
        }
        this.loadingStations = true;
        const params = [
            `paginacion=${false}`,
            `idPuntoVenta=${office.id}`
        ];
        this.saleStationService.findAllList(params.join('&'))
            .subscribe(data => {
                const estacionFija = this.loginService.getUser().estacionVenta;
                this.stations = estacionFija ? data.filter(ev => ev.id === estacionFija.id) : data;
                this.stationSel = (keepCurrent && this.stations.find(s => s.id === keepCurrent.id)) || this.stations[0];
                this.loadingStations = false;
                if (dispatchOnLoad) {
                    this.dispatchStation();
                }
            }, () => this.loadingStations = false);
    }

}
